import { p, h, ul, code, tip, info, warn, table, steps } from './helpers';

const course = {
  id: 'routing',
  step: 5,
  title: 'Routing SIP with Kamailio',
  tagline: 'Build a real registrar and proxy: initial checks, in-dialog routing, location, replies and failover.',
  icon: 'Route',
  colors: ['#ffcf3d', '#ff5b3d'],
  level: 'Intermediate',
  lessons: [
    {
      id: 'stateless-vs-stateful',
      title: 'Stateless vs stateful forwarding',
      icon: 'ToggleRight',
      minutes: 8,
      summary: 'When to use sl and forward(), and why almost every config relies on tm and t_relay().',
      blocks: [
        table(['', 'Stateless: sl / forward()', 'Stateful: tm / t_relay()'], [
          ['Memory per request', 'None', 'A transaction in shared memory'],
          ['Retransmissions', 'Forwarded as they arrive', 'Absorbed and generated by Kamailio'],
          ['100 Trying for INVITE', 'No', 'Yes, automatically'],
          ['Forking and failover', 'No', 'Yes: parallel branches, failure_route'],
          ['CANCEL matching', 'Best effort', 'Exact, per transaction'],
          ['Accounting of call results', 'No', 'Yes, with the acc module'],
        ]),
        code('cfg', `
# stateless reply: nothing is remembered afterwards
sl_send_reply("403", "Forbidden");

# stateful reply: create the transaction first, then answer
t_newtran();
t_reply("486", "Busy Here");

# stateful forwarding: the normal way
if (!t_relay()) {
    sl_reply_error();       # reply with the reason t_relay failed
}

# send to an explicit destination
t_relay_to_udp("10.0.0.20", "5060");
$du = "sip:10.0.0.20:5060;transport=tcp";
t_relay();`),
        h('Important tm parameters'),
        table(['Parameter', 'Default', 'Meaning'], [
          ['`fr_timer`', '30000 ms', 'How long to wait for a final reply to a non-INVITE (and for any reply to an INVITE)'],
          ['`fr_inv_timer`', '120000 ms', 'Max time to wait for a final reply after a provisional one: ringing time'],
          ['`restart_fr_on_each_reply`', '1', 'Restart fr_inv_timer on each new provisional reply'],
          ['`auto_inv_100`', '1', 'Send 100 Trying automatically for INVITE'],
        ]),
        p('You can change timers per transaction from the script: `t_set_fr(60000, 5000);` sets 60 s ringing and 5 s for the first reply on the next branches.'),
        tip('Rule of thumb', 'Use stateless replies (`sl_send_reply`) for quick rejections at the top of the script, before you have spent any memory on the request. Use `t_relay()` for everything you forward.'),
      ],
    },
    {
      id: 'reqinit',
      title: 'Initial checks: route[REQINIT]',
      icon: 'ShieldCheck',
      minutes: 9,
      summary: 'Reject bad, looping and hostile traffic before it costs you anything.',
      blocks: [
        p('The default config starts every request with a block of cheap checks. Here it is with flood protection and scanner blocking included.'),
        code('cfg', `
route[REQINIT] {
    # block known scanners by User-Agent
    if ($ua =~ "friendly-scanner|sipcli|sipvicious|VaxSIPUserAgent") {
        exit;   # drop silently, do not help them
    }

#!ifdef WITH_ANTIFLOOD
    # banned IPs are dropped (see Step 6)
    if (src_ip != myself) {
        if ($sht(ipban=>$si) != $null) {
            exit;
        }
        if (!pike_check_req()) {
            xlog("L_ALERT", "pike blocking $rm from $fu ($si:$sp)\\n");
            $sht(ipban=>$si) = 1;
            exit;
        }
    }
#!endif

    # loop protection
    if (!mf_process_maxfwd_header("10")) {
        sl_send_reply("483", "Too Many Hops");
        exit;
    }

    # answer keepalive OPTIONS sent to us
    if (is_method("OPTIONS") && uri == myself && $rU == $null) {
        sl_send_reply("200", "Keepalive");
        exit;
    }

    # malformed messages
    if (!sanity_check("17895", "7")) {
        xlog("L_WARN", "malformed request from $si:$sp\\n");
        exit;
    }
}`),
        ul([
          '`mf_process_maxfwd_header("10")` decrements Max-Forwards, or adds it with value 10 if missing, and returns false when it reaches zero.',
          '`sanity_check()` validates the Request-URI, CSeq, Content-Length, Via and more. The two bitmasks pick which checks run for the message and for URIs.',
          '`uri == myself` is true when the R-URI host matches a local IP or an `alias`.',
        ]),
        warn('Drop, do not reply, to attackers', 'Every reply you send to a scanner confirms that a SIP server lives at that address. Silent `exit` gives them nothing to work with.'),
      ],
    },
    {
      id: 'in-dialog',
      title: 'Record-Route and in-dialog requests',
      icon: 'Repeat',
      minutes: 11,
      summary: 'How Kamailio stays in the path and routes ACK, BYE and re-INVITE with loose_route().',
      blocks: [
        p('A request with a To tag belongs to an existing dialog. These requests should follow the Route set created by Record-Route during call setup, not be routed from scratch.'),
        code('cfg', `
route[WITHINDLG] {
    if (!has_totag()) return;          # initial request: go back

    # sequential request within a dialog should
    # take the path determined by record-routing
    if (loose_route()) {
        route(DLGURI);
        if (is_method("BYE")) {
            setflag(FLT_ACC);          # account the end of the call
            setflag(FLT_ACCFAILED);
        } else if (is_method("ACK")) {
            route(NATMANAGE);          # ACK may carry SDP
        } else if (is_method("NOTIFY")) {
            record_route();            # NOTIFY can refresh the route set
        }
        route(RELAY);
        exit;
    }

    if (is_method("ACK")) {
        if (t_check_trans()) {
            # ACK for a negative reply that we forwarded statefully
            route(RELAY);
            exit;
        }
        exit;                          # ACK without matching transaction
    }
    sl_send_reply("404", "Not here");
    exit;
}

route[DLGURI] {
#!ifdef WITH_NAT
    if (!isdsturiset()) {
        handle_ruri_alias();           # resolve NAT alias in the R-URI
    }
#!endif
    return;
}`),
        h('Adding Record-Route on initial requests'),
        code('cfg', `
# in request_route, for initial requests only
remove_hf("Route");                    # drop preloaded Route headers
if (is_method("INVITE|SUBSCRIBE")) {
    record_route();
}`),
        info('Why only INVITE and SUBSCRIBE?', 'Only dialog-forming requests need Record-Route. Adding it to REGISTER or OPTIONS is harmless but pointless, since they never create dialogs.'),
        h('Double Record-Route'),
        p('When Kamailio receives on one socket and sends on another (UDP to TCP, or a private interface to a public one), `record_route()` automatically inserts two Record-Route headers, one for each side. This is controlled by the `enable_double_rr` parameter of the rr module, which is on by default.'),
        tip('Loose routing in one sentence', '`loose_route()` returns true when the top Route header points to this server. It removes that header and lets the next Route, or the R-URI, determine where the request goes next.'),
      ],
    },
    {
      id: 'registrar-location',
      title: 'Registrar and location lookup',
      icon: 'MapPin',
      minutes: 12,
      summary: 'Save contacts on REGISTER, find them on INVITE, and ring every device in parallel.',
      blocks: [
        code('cfg', `
loadmodule "usrloc.so"
loadmodule "registrar.so"

modparam("usrloc", "db_mode", 2)            # write-back to the DB
modparam("usrloc", "db_url", DBURL)
modparam("usrloc", "use_domain", 0)         # 1 for multi-domain
modparam("usrloc", "nat_bflag", FLB_NATB)
modparam("registrar", "method_filtering", 1)
modparam("registrar", "max_contacts", 10)
modparam("registrar", "default_expires", 3600)`, 'Module setup'),
        table(['usrloc db_mode', 'Behavior'], [
          ['0', 'Memory only. Fastest. Registrations are lost on restart.'],
          ['1', 'Write-through. Every change goes to the DB immediately.'],
          ['2', 'Write-back. Changes are flushed periodically (`timer_interval`). Good balance.'],
          ['3', 'DB-only. No memory cache. Lets several Kamailio servers share one location table.'],
        ]),
        h('Handling REGISTER'),
        code('cfg', `
route[REGISTRAR] {
    if (!is_method("REGISTER")) return;

    if (isflagset(FLT_NATS)) {
        setbflag(FLB_NATB);
#!ifdef WITH_NATSIPPING
        setbflag(FLB_NATSIPPING);      # keep the pinhole open with OPTIONS
#!endif
    }
    if (!save("location")) {
        sl_reply_error();
    }
    exit;
}`),
        p('`save("location")` stores or updates the bindings from the Contact headers and sends the 200 OK itself. Authentication (Step 6) happens before this route runs.'),
        h('Finding the callee'),
        code('cfg', `
route[LOCATION] {
    if (!lookup("location")) {
        $var(rc) = $rc;
        t_newtran();
        switch ($var(rc)) {
            case -1:
            case -3:
                send_reply("404", "Not Found");
                exit;
            case -2:
                send_reply("405", "Method Not Allowed");
                exit;
        }
    }
    # when routing via usrloc, log the missed calls also
    if (is_method("INVITE")) {
        setflag(FLT_ACCMISSED);
    }
    route(RELAY);
    exit;
}`),
        table(['lookup() result', 'Meaning'], [
          ['1 (true)', 'Found. The R-URI now holds the first contact; other contacts became extra branches.'],
          ['-1', 'No contact found for the AoR'],
          ['-2', 'Contacts found, but none supports this method (with `method_filtering`)'],
          ['-3', 'Internal error'],
        ]),
        tip('Debugging lookups', 'If calls return 404 while the user appears registered, compare `kamctl ul show` with the R-URI. A domain mismatch with `use_domain=1`, or a username case difference, is the usual culprit.'),
      ],
    },
    {
      id: 'replies-failures',
      title: 'Relaying, replies and failure routes',
      icon: 'LifeBuoy',
      minutes: 11,
      summary: 'Arm callbacks before relaying, and turn busy or unanswered calls into voicemail or failover.',
      blocks: [
        code('cfg', `
route[RELAY] {
    # arm branch, reply and failure handling once per transaction
    if (is_method("INVITE|BYE|SUBSCRIBE|UPDATE")) {
        if (!t_is_set("branch_route")) t_on_branch("MANAGE_BRANCH");
    }
    if (is_method("INVITE|SUBSCRIBE|UPDATE")) {
        if (!t_is_set("onreply_route")) t_on_reply("MANAGE_REPLY");
    }
    if (is_method("INVITE")) {
        if (!t_is_set("failure_route")) t_on_failure("MANAGE_FAILURE");
    }

    if (!t_relay()) {
        sl_reply_error();
    }
    exit;
}`),
        h('Serial forking: try voicemail when busy'),
        code('cfg', `
failure_route[MANAGE_FAILURE] {
    route(NATMANAGE);

    if (t_is_canceled()) exit;        # caller hung up, nothing to do

    # 486 busy, 408 timeout, 480 unavailable -> voicemail
    if (t_check_status("486|408|480")) {
        $ru = "sip:vm-" + $rU + "@10.0.0.30:5060";
        $du = $null;
        t_on_failure("VM_FAILED");
        route(RELAY);
        exit;
    }
}

failure_route[VM_FAILED] {
    if (t_is_canceled()) exit;
    xlog("L_ERR", "voicemail failed with $T_reply_code for $ci\\n");
}`),
        ul([
          'Inside a failure_route you may change `$ru` and `$du` and call `t_relay()` again. Kamailio adds a new branch to the **same** transaction.',
          'The caller sees only one final response: either the new branch\'s answer, or the best failure when no branch succeeds.',
          'A failure_route is a one-shot callback. Arm it again with `t_on_failure()` if you want to handle failures of the new branch.',
        ]),
        h('Inspecting replies'),
        code('cfg', `
onreply_route[MANAGE_REPLY] {
    xdbg("incoming reply $rs $rr\\n");
    if (status =~ "[12][0-9][0-9]") {
        route(NATMANAGE);             # fix Contact and SDP of 18x and 2xx
    }
}`),
        warn('Do not wait forever', 'Lower `fr_inv_timer` (or call `t_set_fr()`) on calls that should roll over to voicemail. With the default 120 seconds the caller hears ringing for two minutes before your failure_route ever runs.'),
      ],
    },
    {
      id: 'request-route-walkthrough',
      title: 'Walkthrough: the full request_route',
      icon: 'Map',
      minutes: 10,
      summary: 'The default request_route in order, with the reason for every step.',
      blocks: [
        steps([
          { title: 'REQINIT', text: 'Drop scanners, flood sources, loops and malformed messages. Answer keepalive OPTIONS.' },
          { title: 'NATDETECT', text: 'Check whether the sender is behind NAT and fix the Contact of REGISTERs.' },
          { title: 'CANCEL', text: 'Match it to its INVITE transaction and relay it statefully, or drop it.' },
          { title: 'Retransmissions', text: '`t_precheck_trans()` and `t_check_trans()` absorb retransmitted requests that already have a transaction.' },
          { title: 'WITHINDLG', text: 'Route requests with a To tag using the Route set, then stop.' },
          { title: 'AUTH', text: 'Challenge REGISTERs and calls from local users. Refuse to be an open relay.' },
          { title: 'Record-Route', text: 'Remove preloaded Route headers and record-route dialog-forming requests.' },
          { title: 'SIPOUT', text: 'Send requests for foreign domains out to the internet.' },
          { title: 'REGISTRAR', text: 'Save registrations and stop.' },
          { title: 'LOCATION', text: 'Find the callee, then relay to all of their devices.' },
        ]),
        code('cfg', `
request_route {
    route(REQINIT);
    route(NATDETECT);

    if (is_method("CANCEL")) {
        if (t_check_trans()) {
            route(RELAY);
        }
        exit;
    }

    if (!is_method("ACK")) {
        if (t_precheck_trans()) {
            t_check_trans();
            exit;
        }
        t_check_trans();
    }

    route(WITHINDLG);

    ### only initial requests (no To tag) below this line
    route(AUTH);

    remove_hf("Route");
    if (is_method("INVITE|SUBSCRIBE")) {
        record_route();
    }

    if (is_method("INVITE")) {
        setflag(FLT_ACC);
    }

    route(SIPOUT);
    route(REGISTRAR);

    if ($rU == $null) {
        sl_send_reply("484", "Address Incomplete");
        exit;
    }

    route(LOCATION);
}`),
        info('Read the real thing', 'The full default config ships as `/etc/kamailio/kamailio.cfg` and is in the Kamailio source tree under `etc/kamailio.cfg`. It includes extra blocks for presence, XMLRPC/JSONRPC, PSTN gateways and voicemail. Now that you know the skeleton, read it top to bottom; it is the best tutorial there is.'),
      ],
    },
  ],
  quiz: [
    { q: 'What does loose_route() do for an in-dialog request?', options: ['Looks up the user in the location table', 'Processes the Route headers and sets the next hop from them', 'Adds a Record-Route header', 'Authenticates the request'], answer: 1, explain: 'loose_route() checks whether the top Route points to this server, removes it and routes on the remaining Route set or the R-URI.' },
    { q: 'lookup("location") returns -1. What does that mean?', options: ['Contact found', 'No contact found for the user', 'Method not supported', 'Database error'], answer: 1, explain: '-1 means not found, -2 method not supported, -3 internal error.' },
    { q: 'Which usrloc db_mode lets several Kamailio servers share one location table?', options: ['0', '1', '2', '3'], answer: 3, explain: 'db_mode 3 is DB-only: every lookup reads the database, so all servers see the same data.' },
    { q: 'Where can you call t_relay() again to try a second destination in the same transaction?', options: ['onreply_route', 'failure_route', 'onsend_route', 'reply_route'], answer: 1, explain: 'A failure_route can change $ru or $du and relay a new branch of the same transaction.' },
    { q: 'Why check t_is_canceled() at the start of a failure_route?', options: ['To save memory', 'Because the caller already hung up and no new branch should start', 'It is required by sanity_check', 'To enable accounting'], answer: 1, explain: 'If the caller cancelled, the failure is expected and you must not ring voicemail or another target.' },
  ],
};

export default course;
